import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CaretIcon, CloseIcon, SearchIcon } from '../assets/icons';
import { Spinner } from './ui';
import {
  useGetEcTreeQuery,
  useGetLocationTreeQuery,
  type EcTreeNode,
} from '../features/territory/territoryApi';

/** The chosen area — this is what gets saved as the location. */
export interface CascadeSelection {
  level: string;
  /** EC level id/rank of the chosen node (from the ec-tree). */
  levelRank: number;
  value: string;
  label: string;
}

/** Stable key for an EC-tree node (its area code, falling back to the name). */
const ecNodeKey = (n: EcTreeNode) => n.code || n.name || '';

/** Single-select EC location tree, rendered inside the dropdown panel. */
function LocationTree({
  nodes,
  selectedKey,
  pathKeys,
  onSelect,
}: {
  nodes: EcTreeNode[];
  selectedKey: string;
  pathKeys: Set<string>;
  onSelect: (key: string) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const render = (node: EcTreeNode, depth: number, path: string): ReactNode => {
    const key = ecNodeKey(node);
    const kids = node.children ?? [];
    const open = !collapsed.has(path);
    const isSel = key !== '' && selectedKey === key;
    const onPath = key !== '' && !isSel && pathKeys.has(key);
    return (
      <div key={path}>
        <div className="flex items-center gap-1" style={{ paddingLeft: depth * 14 }}>
          {kids.length > 0 ? (
            <button
              type="button"
              onClick={() => toggle(path)}
              aria-label={open ? 'Collapse' : 'Expand'}
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
            >
              <CaretIcon className={['size-3.5 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => onSelect(key)}
            title={node.name || key}
            className={[
              'flex-1 truncate rounded-md py-1.5 pr-2 text-left text-sm transition-colors',
              isSel
                ? 'bg-brand-shade font-semibold text-brand'
                : onPath
                  ? 'font-medium text-brand hover:bg-brand-shade/60'
                  : 'text-ink hover:bg-brand-shade/60',
            ].join(' ')}
          >
            {/* Node name and its level in distinct colours for easy identification. */}
            {node.name || key}
            {node.level ? (
              <span className="ml-1.5 rounded bg-[#fff7ed] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#c2410c]">
                {node.level}
              </span>
            ) : null}
          </button>
        </div>
        {kids.length > 0 && open && kids.map((c, i) => render(c, depth + 1, `${path}.${i}`))}
      </div>
    );
  };
  if (nodes.length === 0) {
    return <p className="px-2 py-3 text-sm text-ink-secondary">No locations available.</p>;
  }
  return <div className="flex flex-col gap-0.5">{nodes.map((n, i) => render(n, 0, `n${i}`))}</div>;
}

/**
 * Location picker for the elected / bureaucracy directories. Renders the organization's
 * location tree (GET /territory/location-tree/?organization=<id>), which starts at the
 * org's boundary and drills down (Parliament → District → Assembly → …). Single-select
 * with server-side search; the chosen node's code + level + full breadcrumb (shown in the
 * field as "a → b → c") are reported via `onChange`.
 */
export default function LocationCascade({
  organization,
  value,
  placeholder = 'Select Location',
  invalid,
  initialLabel,
  source = 'location-tree',
  onChange,
}: {
  organization?: number;
  value: string;
  placeholder?: string;
  invalid?: boolean;
  /** Saved label shown until the tree resolves the breadcrumb (edit mode). */
  initialLabel?: string;
  /**
   * Which tree to render:
   *  - 'location-tree' (default): boundary-scoped Position-Location tree.
   *  - 'ec-tree': the org's full EC tree (GET /territory/ec-tree/?booths=false).
   */
  source?: 'location-tree' | 'ec-tree';
  onChange: (sel: CascadeSelection | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Server-side search: debounce the typed text into the query's `?search=` param.
  useEffect(() => {
    const t = setTimeout(() => setSearch(queryText.trim()), 300);
    return () => clearTimeout(t);
  }, [queryText]);

  const isEcTree = source === 'ec-tree';
  const locTreeQ = useGetLocationTreeQuery(
    { organization: organization!, ...(search ? { search } : {}) },
    { skip: organization == null || isEcTree },
  );
  const ecTreeQ = useGetEcTreeQuery(
    { organization: organization!, booths: false, ...(search ? { search } : {}) },
    { skip: organization == null || !isEcTree },
  );
  const ecTree = isEcTree ? ecTreeQ.data : locTreeQ.data;
  const isFetching = isEcTree ? ecTreeQ.isFetching : locTreeQ.isFetching;

  // For each node key: breadcrumb label, ancestry keys, level name, and EC level id.
  const ecPaths = useMemo(() => {
    const labelPath: Record<string, string> = {};
    const keyPath: Record<string, string[]> = {};
    const levelOf: Record<string, string> = {};
    const rankOf: Record<string, number> = {};
    const walk = (n: EcTreeNode, ancNames: string[], ancKeys: string[]) => {
      const k = ecNodeKey(n);
      const names = [...ancNames, n.name || k];
      const keys = [...ancKeys, k];
      if (k) {
        labelPath[k] = names.filter(Boolean).join(' → ');
        keyPath[k] = keys.filter(Boolean);
        levelOf[k] = n.level || '';
        if (n.ecLevelId != null) rankOf[k] = n.ecLevelId;
      }
      n.children?.forEach((c) => walk(c, names, keys));
    };
    (ecTree ?? []).forEach((n) => walk(n, [], []));
    return { labelPath, keyPath, levelOf, rankOf };
  }, [ecTree]);

  // The field shows the full breadcrumb of the selected node ("a → b → c").
  // Show the resolved breadcrumb; while the tree is still loading show "Loading…" instead of
  // the raw value/id, then fall back to initialLabel or the value once loaded.
  const valueLabel = value
    ? ecPaths.labelPath[value] || (isFetching ? 'Loading…' : initialLabel || value)
    : '';

  return (
    <div ref={ref} className="relative">
      <div
        className={[
          'flex h-11 w-full items-center gap-2 rounded-lg border bg-white px-[13px] text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors focus-within:ring-2',
          organization == null ? 'bg-[#f8fafc]' : '',
          invalid
            ? 'border-red-400 focus-within:ring-red-100'
            : 'border-[#e2e8f0] focus-within:border-brand focus-within:ring-brand/15',
        ].join(' ')}
      >
        <SearchIcon className="size-4 shrink-0 text-[#94a3b8]" />
        {/* Search directly in the field: shows the typed query while open, the selected
            breadcrumb (a → b → c) when closed. */}
        <input
          value={open ? queryText : valueLabel}
          disabled={organization == null}
          placeholder={
            valueLabel || (organization == null ? 'Select an organization first' : placeholder)
          }
          onFocus={() => {
            setOpen(true);
            setQueryText('');
          }}
          onChange={(e) => {
            setQueryText(e.target.value);
            if (!open) setOpen(true);
          }}
          // Override the global native-input border/bg (index.css) so it doesn't render
          // as a second boxed input inside the field.
          className="min-w-0 flex-1 truncate !h-auto !rounded-none !border-0 !bg-transparent px-0 text-ink placeholder:text-[#94a3b8] focus:!shadow-none focus:outline-none disabled:cursor-not-allowed"
        />
        {value && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              setQueryText('');
              onChangeRef.current(null);
            }}
            className="flex size-4 shrink-0 items-center justify-center rounded text-[#9ca3af] transition-colors hover:text-ink"
          >
            <CloseIcon className="size-3" />
          </span>
        )}
        <button
          type="button"
          aria-label={open ? 'Collapse' : 'Expand'}
          onClick={() => setOpen((o) => !o)}
          className="flex shrink-0 items-center"
        >
          <CaretIcon
            className={['size-4 text-[#9ca3af] transition-transform', open ? 'rotate-180' : ''].join(' ')}
          />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-[60vh] w-full min-w-[320px] overflow-auto rounded-xl border border-line bg-white p-3 shadow-[0_12px_32px_rgba(0,0,0,0.14)]">
          {isFetching ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-ink-secondary">
              <Spinner size={16} /> Loading…
            </div>
          ) : (
            <LocationTree
              nodes={ecTree ?? []}
              selectedKey={value}
              pathKeys={new Set(ecPaths.keyPath[value] ?? [])}
              onSelect={(key) => {
                if (!key) return;
                onChangeRef.current({
                  value: key,
                  level: ecPaths.levelOf[key] || '',
                  levelRank: ecPaths.rankOf[key] ?? 0,
                  label: ecPaths.labelPath[key] || key,
                });
                setQueryText('');
                setOpen(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
